import { useState } from 'react';
import { Alert } from '../../components/Overlay.tsx';
import { Avatar } from '../../components/Avatar.tsx';
import { Icon } from '../../components/Icon.tsx';
import { ApiError, isLive, signOut } from '../../api/client.ts';
import { deleteAccount } from '../../api/me.ts';

/**
 * 내 계정 — Home 맨 아래.
 *
 * **기획서에 없는 자리입니다.** 로그아웃은 네이티브 셸의 몫이었고(10절의
 * 브리지 «토큰 만료·로그아웃»), 웹앱은 토큰이 이미 있다고 가정하고 시작했기
 * 때문입니다. 셸 없이 웹앱만으로 쓰기로 하면서 그 책임이 이쪽으로 넘어왔습니다 —
 * 나갈 문이 없으면 계정을 바꿀 수도, 남의 기기에서 내려올 수도 없습니다.
 *
 * Home 맨 아래에 둔 것은 자주 누를 것이 아니기 때문입니다. 탭바에 자리를
 * 내주거나 상단 버튼을 차지하면, 하루에 한 번도 안 쓰는 것이 매번 보입니다.
 */

export function Account({ name }: { name: string }) {
  const [confirming, setConfirming] = useState(false);
  const [erasing, setErasing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const erase = async () => {
    setBusy(true);
    setFailure(null);
    try {
      // 성공하면 토큰이 사라지고 App 이 로그인 화면으로 되돌립니다
      await deleteAccount();
    } catch (e) {
      // 그룹장으로 있는 모임이 남아 있으면 서버가 그 이름을 담아 거절합니다
      setFailure(e instanceof ApiError ? e.message : '계정을 지우지 못했습니다.');
      setBusy(false);
      setErasing(false);
    }
  };

  // 목업으로 둘러보는 중에는 나갈 곳이 없습니다
  if (!isLive()) return null;

  return (
    <>
      <div className="account">
        <Avatar name={name} seed={name} />
        <div className="account__who">
          <p className="row__name">{name}</p>
          <p className="row__sub">카카오로 로그인함</p>
        </div>
        <button type="button" className="account__out" onClick={() => setConfirming(true)}>
          <Icon name="forward" size={16} />
          로그아웃
        </button>
      </div>

      {/*
        계정 삭제 — 앱스토어 심사 지침 5.1.1(v). 계정을 만드는 앱은 앱 안에서
        지울 수 있어야 합니다. 조용한 글씨로 두되 숨기지는 않습니다 —
        찾을 수 없으면 없는 것과 같습니다.
      */}
      <button type="button" className="account__erase" onClick={() => setErasing(true)}>
        계정 삭제
      </button>

      {failure && <p className="failure">{failure}</p>}

      {confirming && (
        <Alert
          title="로그아웃할까요?"
          onClose={() => setConfirming(false)}
          actions={
            <>
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setConfirming(false)}
              >
                취소
              </button>
              <button type="button" className="btn btn--danger" onClick={signOut}>
                로그아웃
              </button>
            </>
          }
        >
          {/* 지워지지 않는다는 것을 먼저 적습니다 — 로그아웃을 삭제로 읽는 사람이 있습니다 */}
          <p>
            기도 노트는 <b>그대로 남습니다.</b> 다시 카카오로 로그인하면 다 보입니다.
          </p>
        </Alert>
      )}

      {erasing && (
        <Alert
          title="계정을 지울까요?"
          onClose={() => setErasing(false)}
          actions={
            <>
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setErasing(false)}
              >
                취소
              </button>
              <button
                type="button"
                className="btn btn--danger"
                disabled={busy}
                onClick={() => void erase()}
              >
                {busy ? '지우는 중…' : '계정 삭제'}
              </button>
            </>
          }
        >
          {/* 되돌릴 수 없는 쪽과 남는 쪽을 나눠 적습니다 */}
          <p>
            <b>되돌릴 수 없습니다.</b> 기도 노트 · 진행 기록 · 친구 관계 · 모임에 적은
            기도 제목이 모두 지워집니다.
          </p>
          <p>남이 내 노트에 남긴 이모티콘과 코멘트도 함께 사라집니다.</p>
        </Alert>
      )}
    </>
  );
}
